import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getDocuments, getUploadUrls, uploadToS3, markReady, bulkDeleteDocuments, ingestDocuments } from '../../services/documentService';

const ACCEPTED_TYPES = '.pdf,.doc,.docx,.txt,.md,.csv,.xls,.xlsx';
const MAX_BATCH = 100;

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const STATUS_STYLE = {
  pending_upload:    { color: '#479EC3', bg: 'rgba(71,158,195,0.1)',  border: 'rgba(71,158,195,0.25)',  label: 'Pending' },
  ready_to_ingest:   { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.25)',  label: 'Queued' },
  ingest_in_process: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.25)',  label: 'Processing' },
  ingested:          { color: '#10b981', bg: 'rgba(16,185,129,0.1)',  border: 'rgba(16,185,129,0.25)',  label: 'Ingested' },
  deleted:           { color: '#475569', bg: 'rgba(71,85,105,0.1)',   border: 'rgba(71,85,105,0.2)',    label: 'Deleted' },
  error:             { color: '#f87171', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.2)',   label: 'Error' },
};

function Checkbox({ checked, indeterminate, onChange }) {
  return (
    <div onClick={onChange} style={{
      width: 15, height: 15, borderRadius: 4, flexShrink: 0, cursor: 'pointer',
      border: `1.5px solid ${checked || indeterminate ? '#479EC3' : 'rgba(71,158,195,0.3)'}`,
      background: checked || indeterminate ? 'rgba(71,158,195,0.15)' : 'transparent',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'background .15s, border-color .15s',
    }}>
      {checked && (
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      )}
      {!checked && indeterminate && (
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="3.5" strokeLinecap="round">
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
      padding: '3px 8px', borderRadius: 5,
      color: s.color, background: s.bg, border: `1px solid ${s.border}`,
      flexShrink: 0,
    }}>{s.label}</span>
  );
}

export default function DocumentUpload() {
  const { user } = useAuth();

  const [docs, setDocs]           = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [docsError, setDocsError]  = useState('');

  const [pending, setPending]      = useState([]);   // File objects queued for upload
  const [uploading, setUploading]  = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [fileProgress, setFileProgress] = useState({}); // { fileName: 'uploading'|'done'|'error' }

  const [deletingId, setDeletingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [ingestMsg, setIngestMsg] = useState('');
  const [ingestMsgType, setIngestMsgType] = useState('success'); // 'success' | 'error'
  const [ingestionAllowed, setIngestionAllowed] = useState(true);
  const [ingestionStatus, setIngestionStatus] = useState(null);
  const [docTrainings, setDocTrainings] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);

  const loadDocs = useCallback(async () => {
    if (!user) return;
    try {
      const { documents, ingestionAllowed: allowed, ingestionStatus: status, docTrainings: trainings } = await getDocuments();
      setDocs(documents);
      setIngestionAllowed(allowed);
      setIngestionStatus(status);
      setDocTrainings(trainings ?? null);
    } catch {
      setDocsError('Failed to load documents.');
    } finally {
      setDocsLoading(false);
    }
  }, [user]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  useEffect(() => {
    if (!ingestionStatus?.isRunning) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        pollCountRef.current = 0;
      }
      const { lastRunStatus, lastCompletedAt } = ingestionStatus || {};
      const isRecent = lastCompletedAt &&
        (Date.now() - new Date(lastCompletedAt).getTime()) < 2 * 60 * 60 * 1000;
      if (!isRecent || !lastRunStatus) {
        setIngestMsg('');
      } else if (lastRunStatus === 'COMPLETED') {
        setIngestMsgType('success');
        setIngestMsg('Last ingestion was completed.');
      } else if (lastRunStatus === 'FAILED') {
        setIngestMsgType('error');
        setIngestMsg('Last ingestion run failed. Please try again.');
      } else {
        setIngestMsg('');
      }
      return;
    }
    pollCountRef.current = 0;
    pollRef.current = setInterval(async () => {
      pollCountRef.current += 1;
      if (pollCountRef.current >= 30) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        pollCountRef.current = 0;
        return;
      }
      await loadDocs();
    }, 60_000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        pollCountRef.current = 0;
      }
    };
  }, [ingestionStatus?.isRunning, ingestionStatus?.lastCompletedAt, ingestionStatus?.lastRunStatus, loadDocs]);

  const addFiles = useCallback((incoming) => {
    const arr = Array.from(incoming);
    setUploadError('');
    setPending(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      const novel = arr.filter(f => !existing.has(f.name + f.size));
      const slots = MAX_BATCH - prev.length;
      if (slots <= 0) {
        setUploadError(`Batch limit reached — max ${MAX_BATCH} files per upload.`);
        return prev;
      }
      if (novel.length > slots) {
        const skipped = novel.length - slots;
        setUploadError(`${skipped} file${skipped !== 1 ? 's' : ''} not added — you can only upload ${MAX_BATCH} per batch.`);
        return [...prev, ...novel.slice(0, slots)];
      }
      return [...prev, ...novel];
    });
  }, []);

  function removePending(idx) {
    setPending(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleUpload() {
    if (!pending.length || uploading) return;
    setUploadError('');
    setUploading(true);
    setFileProgress({});

    // outcomes tracks final status per filename so we can filter pending after
    const outcomes = {};

    let urlFiles = [], urlErrors = [];
    try {
      const result = await getUploadUrls(pending);
      urlFiles = result.files;
      urlErrors = result.errors;
    } catch (err) {
      setUploadError(err.message);
      setUploading(false);
      return;
    }

    // Apply per-file errors from the batch response
    const initialProgress = {};
    urlErrors.forEach(({ fileName, error }) => {
      const status = /hash already exists|already exists/i.test(error) ? 'exists' : 'error';
      outcomes[fileName] = status;
      initialProgress[fileName] = status;
    });
    setFileProgress(initialProgress);

    // Upload files that got a presigned URL
    await Promise.all(
      urlFiles.map(async ({ docId, fileName, uploadUrl }) => {
        const file = pending.find(f => f.name === fileName);
        if (!file) return;
        setFileProgress(p => ({ ...p, [fileName]: 'uploading' }));
        try {
          await uploadToS3(uploadUrl, file);
          await markReady(docId);
          outcomes[fileName] = 'done';
          setFileProgress(p => ({ ...p, [fileName]: 'done' }));
        } catch {
          outcomes[fileName] = 'error';
          setFileProgress(p => ({ ...p, [fileName]: 'error' }));
        }
      })
    );

    const uploaded = Object.values(outcomes).filter(v => v === 'done').length;
    const skipped  = Object.values(outcomes).filter(v => v === 'exists').length;

    if (uploaded > 0) await loadDocs();
    setPending(prev => prev.filter(f => outcomes[f.name] !== 'done'));
    if (skipped > 0) setUploadError(`${skipped} file${skipped !== 1 ? 's' : ''} already exist${skipped !== 1 ? '' : 's'} and ${skipped !== 1 ? 'were' : 'was'} skipped.`);
    setUploading(false);
  }

  function toggleOne(docId) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  }

  function toggleAll() {
    const selectable = docs.filter(d => d.status !== 'deleted');
    setSelectedIds(prev =>
      prev.size === selectable.length ? new Set() : new Set(selectable.map(d => d.docId))
    );
  }

  async function handleDelete(docId) {
    setDeletingId(docId);
    setDeleteError('');
    try {
      const { errors } = await bulkDeleteDocuments([docId]);
      if (errors?.length) setDeleteError(errors[0].error);
    } catch {
      // silently ignore
    } finally {
      await loadDocs();
      setDeletingId(null);
    }
  }

  async function handleIngest() {
    if (ingesting) return;
    setIngesting(true);
    setIngestMsg('');
    try {
      const data = await ingestDocuments();
      setIngestMsg(data.message || 'Ingestion queued.');
      await loadDocs();
    } catch (err) {
      setIngestMsg(err.message);
    } finally {
      setIngesting(false);
    }
  }

  async function handleBulkDelete() {
    if (!selectedIds.size || bulkDeleting) return;
    setBulkDeleting(true);
    setDeleteError('');
    try {
      const { errors } = await bulkDeleteDocuments([...selectedIds]);
      if (errors?.length) setDeleteError(`${errors.length} document${errors.length !== 1 ? 's' : ''} could not be deleted.`);
      setSelectedIds(new Set());
    } catch {
      // silently ignore — selection stays so user can retry
    } finally {
      await loadDocs();
      setBulkDeleting(false);
    }
  }

  const totalPendingSize = pending.reduce((s, f) => s + f.size, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Upload section ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <input ref={inputRef} type="file" multiple accept={ACCEPTED_TYPES} style={{ display: 'none' }}
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
          <button onClick={() => inputRef.current?.click()} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '9px 18px', borderRadius: 9, cursor: 'pointer',
            background: 'rgba(71,158,195,0.08)', border: '1.5px dashed rgba(71,158,195,0.4)',
            color: '#479EC3', fontSize: 13.5, fontWeight: 600, transition: 'background .15s, border-color .15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(71,158,195,0.14)'; e.currentTarget.style.borderColor = 'rgba(71,158,195,0.7)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(71,158,195,0.08)'; e.currentTarget.style.borderColor = 'rgba(71,158,195,0.4)'; }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Select Files
          </button>
          <span style={{ fontSize: 12, color: '#475569' }}>PDF, DOC, DOCX, TXT, MD, CSV, XLS, XLSX · <strong style={{ color: '#64748b' }}>10 MB per batch</strong></span>
        </div>

        {pending.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
            {pending.map((file, idx) => {
              const prog = fileProgress[file.name];
              return (
                <div key={idx} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 14px', borderRadius: 8,
                  background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(71,158,195,0.1)',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name}
                  </span>
                  <span style={{ fontSize: 11.5, color: '#475569', flexShrink: 0 }}>{formatBytes(file.size)}</span>
                  {prog === 'uploading' && (
                    <span style={{ fontSize: 11, color: '#479EC3', flexShrink: 0, fontWeight: 600 }}>↑</span>
                  )}
                  {prog === 'done' && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                  {prog === 'exists' && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} title="Already uploaded">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                  )}
                  {prog === 'error' && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  )}
                  {(!prog || prog === 'exists' || prog === 'error') && (
                    <button onClick={() => removePending(idx)} style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#475569', padding: '2px 4px', lineHeight: 1, fontSize: 16, flexShrink: 0,
                      transition: 'color .15s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
                      onMouseLeave={e => e.currentTarget.style.color = '#475569'}
                    >×</button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {pending.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 12.5, color: pending.length >= MAX_BATCH ? '#f59e0b' : '#475569' }}>
              {pending.length}/{MAX_BATCH} files · {formatBytes(totalPendingSize)}
            </span>
            <button onClick={() => { setPending([]); setUploadError(''); setFileProgress({}); }} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
              color: '#f87171', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
              transition: 'background .15s, border-color .15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.25)'; }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              Clear all
            </button>
          </div>
        )}

        {uploadError && (
          <p style={{ fontSize: 13, color: '#f87171', margin: 0, padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
            {uploadError}
          </p>
        )}

        <button onClick={handleUpload} disabled={!pending.length || uploading} style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, alignSelf: 'flex-start',
          padding: '10px 24px', borderRadius: 9, border: 'none',
          cursor: !pending.length || uploading ? 'not-allowed' : 'pointer',
          background: !pending.length ? 'rgba(30,41,59,0.6)' : 'linear-gradient(135deg,#479EC3,#22D3EE)',
          color: !pending.length ? '#334155' : '#fff',
          fontSize: 14, fontWeight: 700, opacity: uploading ? 0.75 : 1,
          transition: 'opacity .2s, background .2s',
        }}>
          {uploading ? (
            <>
              Uploading…
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Upload
            </>
          )}
        </button>
      </div>

      {/* ── Uploaded documents ── */}
      <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', rowGap: 10 }}>
          {!docsLoading && docs.length > 0 && (
            <Checkbox
              checked={selectedIds.size > 0 && selectedIds.size === docs.filter(d => d.status !== 'deleted').length}
              indeterminate={selectedIds.size > 0 && selectedIds.size < docs.filter(d => d.status !== 'deleted').length}
              onChange={toggleAll}
            />
          )}
          <p style={{ fontSize: 11.5, color: '#475569', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', margin: 0, flex: 1 }}>
            Uploaded Documents {!docsLoading && `(${docs.length})`}
          </p>
          {selectedIds.size === 0 && docs.length > 0 && (
            <button
              onClick={handleIngest}
              disabled={ingesting || !ingestionAllowed || ingestionStatus?.isRunning}
              title={
                !ingestionAllowed && docTrainings?.remaining === 0 ? 'Monthly training limit reached' :
                !ingestionAllowed ? 'No new documents to train on' :
                ingestionStatus?.isRunning ? 'Training already in progress' : undefined
              }
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 13px', borderRadius: 6, border: '1px solid rgba(71,158,195,0.3)',
                background: 'rgba(71,158,195,0.08)', color: '#479EC3',
                fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
                cursor: ingesting || !ingestionAllowed || ingestionStatus?.isRunning ? 'not-allowed' : 'pointer',
                opacity: ingesting || !ingestionAllowed || ingestionStatus?.isRunning ? 0.45 : 1,
                transition: 'background .15s, border-color .15s, opacity .15s',
              }}
              onMouseEnter={e => { if (!ingesting && ingestionAllowed && !ingestionStatus?.isRunning) { e.currentTarget.style.background = 'rgba(71,158,195,0.15)'; e.currentTarget.style.borderColor = 'rgba(71,158,195,0.6)'; } }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(71,158,195,0.08)'; e.currentTarget.style.borderColor = 'rgba(71,158,195,0.3)'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
              {ingesting ? 'Queuing…' : ingestionStatus?.isRunning ? 'Running…' : 'Ingest'}
            </button>
          )}
          {docTrainings?.total != null && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                {docTrainings.used}/{docTrainings.total} trainings used
              </span>
              <div style={{ width: 80, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 3,
                  width: `${Math.min(100, (docTrainings.used / docTrainings.total) * 100)}%`,
                  background: docTrainings.remaining === 0 ? '#f87171' : docTrainings.used / docTrainings.total >= 0.7 ? '#fbbf24' : '#22D3EE',
                  transition: 'width 0.4s',
                }} />
              </div>
            </div>
          )}
          {selectedIds.size > 0 && (
            <>
              <span style={{ fontSize: 12, color: '#94a3b8', flexShrink: 0 }}>
                {selectedIds.size} selected
              </span>
              <button onClick={handleBulkDelete} disabled={bulkDeleting} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '5px 12px', borderRadius: 6, cursor: bulkDeleting ? 'not-allowed' : 'pointer',
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                color: '#f87171', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
                opacity: bulkDeleting ? 0.5 : 1,
                transition: 'background .15s, border-color .15s, opacity .15s',
              }}
                onMouseEnter={e => { if (!bulkDeleting) { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.5)'; } }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.25)'; }}
              >
                {bulkDeleting ? (
                  <>
                    Deleting…
                  </>
                ) : (
                  <>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    </svg>
                    Delete selected
                  </>
                )}
              </button>
            </>
          )}
        </div>

        {ingestMsg && (
          <p style={{
            fontSize: 12.5, margin: 0, padding: '7px 12px', borderRadius: 7,
            ...(ingestMsgType === 'success'
              ? { color: '#10b981', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }
              : { color: '#f87171', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }),
          }}>{ingestMsg}</p>
        )}

        {deleteError && (
          <p style={{
            fontSize: 12.5, margin: 0, padding: '7px 12px', borderRadius: 7,
            color: '#f87171', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
          }}>{deleteError}</p>
        )}

        {docsLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569', fontSize: 13 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
              style={{ animation: 'spin 0.8s linear infinite' }}>
              <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
            </svg>
            Loading…
          </div>
        )}

        {docsError && (
          <p style={{ fontSize: 13, color: '#f87171', margin: 0 }}>{docsError}</p>
        )}

        {!docsLoading && !docsError && docs.length === 0 && (
          <p style={{ fontSize: 13, color: '#334155', margin: 0 }}>No documents uploaded yet.</p>
        )}

        {docs.map(doc => {
          const isSelected = selectedIds.has(doc.docId);
          return (
            <div key={doc.docId} className="du-doc-row" style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 8,
              background: isSelected ? 'rgba(71,158,195,0.07)' : 'rgba(15,23,42,0.5)',
              border: `1px solid ${isSelected ? 'rgba(71,158,195,0.3)' : 'rgba(71,158,195,0.1)'}`,
              transition: 'background .15s, border-color .15s',
            }}>
              <Checkbox checked={isSelected} onChange={() => doc.status !== 'deleted' && toggleOne(doc.docId)} />
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
              </svg>
              <span className="du-doc-name" style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={doc.status === 'error' && doc.errorMessage ? doc.errorMessage : undefined}>
                {doc.fileName}
              </span>
              <span className="du-doc-meta" style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <span style={{ fontSize: 11.5, color: '#475569' }}>{formatBytes(doc.fileSize)}</span>
                <span className="du-doc-date" style={{ fontSize: 11.5, color: '#334155' }}>{formatDate(doc.uploadedAt)}</span>
                <StatusBadge status={doc.status} />
              </span>
              {selectedIds.size === 0 && doc.status !== 'deleted' && (
                <button className="du-doc-del" onClick={() => handleDelete(doc.docId)} disabled={deletingId === doc.docId} style={{
                  background: 'none', border: 'none', cursor: deletingId === doc.docId ? 'not-allowed' : 'pointer',
                  color: '#475569', padding: '2px 4px', lineHeight: 1, flexShrink: 0,
                  opacity: deletingId === doc.docId ? 0.4 : 1, transition: 'color .15s',
                }}
                  onMouseEnter={e => { if (deletingId !== doc.docId) e.currentTarget.style.color = '#f87171'; }}
                  onMouseLeave={e => e.currentTarget.style.color = '#475569'}
                  title="Delete document"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                  </svg>
                </button>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 600px) {
          .du-doc-row  { flex-wrap: wrap; row-gap: 8px; }
          .du-doc-name { flex: 1 1 auto; }
          .du-doc-del  { order: 2; }
          .du-doc-meta { order: 3; flex-basis: 100%; padding-left: 25px; justify-content: flex-start; }
        }
      `}</style>
    </div>
  );
}
